import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus, Filter, Trash2, Send, CalendarDays, User, Tag, AlertOctagon, Check, ChevronRight, Search,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { LargeTitle, SegmentedControl, IosSearch, Group, IOS_ACCENT } from "@/components/mobile/ios";
import PullToRefresh from "@/components/mobile/PullToRefresh";
import SwipeableRow from "@/components/mobile/SwipeableRow";
import BottomSheet from "@/components/mobile/BottomSheet";

const STATUSES = [
  { v: "todo", l: "To do" },
  { v: "in_progress", l: "In progress" },
  { v: "review", l: "Review" },
  { v: "done", l: "Done" },
  { v: "blocked", l: "Blocked" },
];
const TASK_STATUS_META = {
  todo: { label: "To do", bg: "#F2F2F7", fg: "#52525B" },
  in_progress: { label: "In progress", bg: "#E6F4FF", fg: "#1F6FEB" },
  review: { label: "Review", bg: "#FFF4E6", fg: "#A56014" },
  done: { label: "Done", bg: "#E7F8EF", fg: "#1BA968" },
  blocked: { label: "Blocked", bg: "#FFE9EC", fg: "#D93B4B" },
};
const PRIORITIES = [
  { v: "low", l: "Low" },
  { v: "medium", l: "Medium" },
  { v: "high", l: "High" },
  { v: "urgent", l: "Urgent" },
];

export default function Tasks() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAssignee, setFilterAssignee] = useState("");
  const [scope, setScope] = useState("all");
  const [query, setQuery] = useState("");
  const [openDetail, setOpenDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const q = {};
    if (filterStatus) q.status = filterStatus;
    if (filterAssignee) q.assignee_id = filterAssignee;
    if (scope) q.scope = scope;
    const r = await api.get("/tasks", { params: q });
    setTasks(r.data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterStatus, filterAssignee, scope]);
  useEffect(() => { api.get("/users").then(r => setUsers(r.data)).catch(()=>{}); }, []);

  // open task via ?open=<id>
  useEffect(() => {
    const id = params.get("open");
    if (id && tasks.length) {
      const t = tasks.find((x) => x.task_id === id);
      if (t) setOpenDetail(t);
    }
  }, [params, tasks]);

  const filtered = useMemo(() => {
    if (!query.trim()) return tasks;
    const q = query.toLowerCase();
    return tasks.filter(t =>
      t.title.toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q) ||
      (t.assignee_name || "").toLowerCase().includes(q)
    );
  }, [tasks, query]);

  const updateTask = async (task_id, patch) => {
    try {
      const r = await api.patch(`/tasks/${task_id}`, patch);
      setTasks((prev) => prev.map(t => t.task_id === task_id ? r.data : t));
      if (openDetail?.task_id === task_id) setOpenDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteTask = async (task_id) => {
    if (!isAdmin) return;
    try {
      await api.delete(`/tasks/${task_id}`);
      setTasks((prev) => prev.filter(t => t.task_id !== task_id));
      setOpenDetail(null);
      toast.success("Task deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Layout
      title="Tasks"
      subtitle="Workstream"
      actions={
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="h-10 px-4 rounded-xl btn-warm text-[13px] font-semibold" data-testid="task-create-button">
              <Plus className="h-4 w-4 mr-1.5" /> New task
            </Button>
          </DialogTrigger>
          <CreateTaskDialog users={users} onCreated={(t) => { setTasks(prev => [t, ...prev]); setCreateOpen(false); }} canAssignOthers={isAdmin} currentUserId={user?.user_id} />
        </Dialog>
      }
    >
      {/* ============ MOBILE iOS view ============ */}
      <div className="lg:hidden">
        <PullToRefresh onRefresh={load}>
          <LargeTitle title="Tasks" />
          <div className="px-4 space-y-3">
            <SegmentedControl
              value={scope}
              onChange={setScope}
              options={[
                { value: "all", label: "All" },
                { value: "mine", label: "Mine" },
                { value: "created", label: "Created" },
                { value: "overdue", label: "Overdue" },
              ]}
            />
            <IosSearch value={query} onChange={setQuery} placeholder="Search tasks" />
          </div>

          {filtered.length === 0 ? (
            <div className="mx-4 mt-6 bg-white rounded-2xl py-12 text-center">
              <div className="text-[15px] text-[#8E8E93]">No tasks match.</div>
              <div className="text-[13px] text-[#8E8E93] mt-1">Tap + to create one.</div>
            </div>
          ) : (
            <div className="mt-6 mx-4 bg-white rounded-2xl overflow-hidden border border-black/[0.04]" style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
              {filtered.map((t, i) => {
                const actions = [];
                if (t.status !== "done") {
                  actions.push({
                    label: "Done",
                    color: "#1BA968",
                    icon: <Check size={20} strokeWidth={2.4} />,
                    onClick: () => updateTask(t.task_id, { status: "done" }),
                    testId: `swipe-done-${t.task_id}`,
                  });
                }
                if (isAdmin) {
                  actions.push({
                    label: "Delete",
                    color: "#D93B4B",
                    icon: <Trash2 size={20} strokeWidth={2.4} />,
                    onClick: () => deleteTask(t.task_id),
                    testId: `swipe-delete-${t.task_id}`,
                  });
                }
                const dColor = t.priority === "urgent" ? "#D93B4B"
                  : t.priority === "high" ? "#E07A1F"
                  : "#C7C7CC";
                const today = new Date(); today.setHours(0,0,0,0);
                const isOverdue = t.due_date && t.status !== "done" && new Date(t.due_date) < today;
                const last = i === filtered.length - 1;
                return (
                  <SwipeableRow
                    key={t.task_id}
                    actions={actions}
                    className={!last ? "border-b border-black/[0.05]" : ""}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenDetail(t)}
                      data-testid={`mobile-task-${t.task_id}`}
                      className="w-full text-left flex items-center gap-3 px-4 py-3.5 active:bg-black/[0.04] transition"
                    >
                      <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: dColor }} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-[16px] font-semibold leading-tight text-[#1C1C1E] ${t.status === "done" ? "line-through text-[#8E8E93]" : ""}`}>
                          {t.title}
                        </div>
                        <div className="text-[13px] text-[#8E8E93] mt-0.5 flex items-center gap-1.5">
                          <CalendarDays size={12} />
                          <span className={isOverdue ? "text-[#D93B4B] font-medium" : ""}>
                            {t.due_date ? new Date(t.due_date).toLocaleDateString() : "No due date"}
                          </span>
                          {t.assignee_name && <span>· {t.assignee_name}</span>}
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-[#C7C7CC] shrink-0" />
                    </button>
                  </SwipeableRow>
                );
              })}
            </div>
          )}

          {/* FAB - mobile */}
          <button
            onClick={() => setCreateOpen(true)}
            className="fixed right-5 bottom-24 h-14 w-14 rounded-full grid place-items-center text-white z-30"
            style={{ background: IOS_ACCENT, boxShadow: "0 10px 22px -6px rgba(224,122,31,0.5), 0 2px 6px rgba(0,0,0,0.1)" }}
            data-testid="task-fab-create-mobile"
            aria-label="New task"
          >
            <Plus size={26} strokeWidth={2.5} />
          </button>

          <div className="h-32" />
        </PullToRefresh>
      </div>

      {/* ============ DESKTOP view (iOS-style) ============ */}
      <div className="hidden lg:block">
      {/* Black-pill segmented control */}
      <div className="soft-card p-2 mb-5 inline-flex">
        <div className="flex items-center gap-1">
          {[
            { v: "all", l: "All" },
            { v: "mine", l: "Assigned to me" },
            { v: "created", l: "Created by me" },
            { v: "overdue", l: "Overdue" },
          ].map((opt) => {
            const isActive = scope === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => setScope(opt.v)}
                data-testid={`tab-${opt.v}`}
                className="px-4 py-2 rounded-xl text-[13px] font-semibold transition"
                style={isActive
                  ? { background: "#1C1C1E", color: "white" }
                  : { color: "#52525B" }
                }
              >
                {opt.l}
              </button>
            );
          })}
        </div>
      </div>

      <div className="soft-card overflow-hidden">
        {/* Search bar inside card header */}
        <div className="px-6 py-4 border-b border-black/[0.05] flex items-center gap-3">
          <Search className="h-4 w-4 text-[#8E8E93]" />
          <input
            placeholder="Search by title, owner, or tag…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="task-search-input"
            className="bg-transparent outline-none text-[14px] placeholder:text-[#8E8E93] flex-1"
          />
          <div className="flex items-center gap-2">
            <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === "__all" ? "" : v)}>
              <SelectTrigger className="h-9 w-[150px] rounded-xl border-0 bg-[#F2F2F7]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All statuses</SelectItem>
                {STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={filterAssignee} onValueChange={(v) => setFilterAssignee(v === "__all" ? "" : v)}>
                <SelectTrigger className="h-9 w-[160px] rounded-xl border-0 bg-[#F2F2F7]" data-testid="filter-assignee">
                  <SelectValue placeholder="Assignee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All assignees</SelectItem>
                  {users.filter(u => u.is_active).map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <span className="text-[12px] text-[#8E8E93] ml-2">{filtered.length} tasks</span>
          </div>
        </div>

        {/* Table header */}
        <div className="grid grid-cols-[1fr_120px_100px_140px_130px_70px] gap-4 px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8E8E93] border-b border-black/[0.05]">
          <div>Title</div>
          <div>Status</div>
          <div>Priority</div>
          <div>Assignee</div>
          <div>Due</div>
          <div className="text-right">Subtasks</div>
        </div>

        {filtered.length === 0 && (
          <div className="px-6 py-12 text-center text-[#8E8E93] text-[14px]">No tasks match the current filters.</div>
        )}
        {filtered.map((t, i) => {
          const meta = TASK_STATUS_META[t.status] || TASK_STATUS_META.todo;
          const pColor = t.priority === "urgent" ? "#D93B4B"
            : t.priority === "high" ? "#E07A1F"
            : t.priority === "medium" ? "#52525B"
            : "#8E8E93";
          const today = new Date(); today.setHours(0,0,0,0);
          const isOverdue = t.due_date && t.status !== "done" && new Date(t.due_date) < today;
          const subDone = (t.subtasks || []).filter(s => s.done).length;
          const subTotal = (t.subtasks || []).length;
          return (
            <div
              key={t.task_id}
              onClick={() => setOpenDetail(t)}
              data-testid={`task-row-${t.task_id}`}
              className={`grid grid-cols-[1fr_120px_100px_140px_130px_70px] gap-4 px-6 py-4 hover:bg-black/[0.02] cursor-pointer items-center ${i !== filtered.length - 1 ? "border-b border-black/[0.04]" : ""}`}
            >
              <div className="min-w-0">
                <div className={`text-[14px] font-semibold text-[#1C1C1E] truncate ${t.status === "done" ? "line-through text-[#8E8E93]" : ""}`}>{t.title}</div>
                <div className="text-[11px] text-[#8E8E93] mt-0.5 truncate">Created by {t.creator_name} · {new Date(t.created_at).toLocaleDateString()}</div>
              </div>
              <div>
                <span className="text-[11px] font-semibold px-2 py-1 rounded-md" style={{ background: meta.bg, color: meta.fg }}>
                  {meta.label}
                </span>
              </div>
              <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: pColor }}>
                {t.priority}
              </div>
              <div className="text-[13px] text-[#52525B] truncate">{t.assignee_name || "—"}</div>
              <div className={`text-[13px] ${isOverdue ? "text-[#D93B4B] font-semibold" : "text-[#52525B]"}`}>
                {t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}
              </div>
              <div className="text-right text-[12px] font-mono text-[#71717A]">{subDone}/{subTotal}</div>
            </div>
          );
        })}
      </div>
      </div>{/* end desktop view */}

      {/* Shared: task detail sheet (works on both mobile and desktop) */}
      <TaskDetailSheet
        task={openDetail}
        users={users}
        isAdmin={isAdmin}
        onClose={() => { setOpenDetail(null); params.delete("open"); setParams(params); }}
        onUpdate={updateTask}
        onDelete={deleteTask}
        currentUser={user}
      />
    </Layout>
  );
}

function Th({ children, className = "" }) {
  return <th className={`py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-neutral-600 ${className}`}>{children}</th>;
}

function DueCell({ date, done }) {
  if (!date) return <span className="text-neutral-400">—</span>;
  const d = new Date(date);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isOverdue = !done && d < today;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[13px] ${isOverdue ? "text-red-700 font-medium" : "text-neutral-700"}`}>
      {isOverdue && <AlertOctagon className="h-3.5 w-3.5" />}
      {d.toLocaleDateString()}
    </span>
  );
}

function CreateTaskDialog({ users, onCreated, canAssignOthers, currentUserId }) {
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", due_date: "", assignee_id: currentUserId || "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/tasks", {
        ...form,
        due_date: form.due_date || null,
      });
      toast.success("Task created");
      onCreated(r.data);
      setForm({ title: "", description: "", priority: "medium", due_date: "", assignee_id: currentUserId || "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="sm:max-w-[520px]">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">New task</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-2">
        <Field label="Title">
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Reconcile Q1 vendor invoices" data-testid="new-task-title" />
        </Field>
        <Field label="Description">
          <Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="new-task-description" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Priority">
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="new-task-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Due date">
            <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="new-task-due" />
          </Field>
        </div>
        {canAssignOthers && (
          <Field label="Assignee">
            <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
              <SelectTrigger data-testid="new-task-assignee"><SelectValue placeholder="Choose member" /></SelectTrigger>
              <SelectContent>
                {users.filter(u => u.is_active).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        )}
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={submitting} data-testid="new-task-submit" className="bg-neutral-950 hover:bg-neutral-800 text-white">
          {submitting ? "Creating…" : "Create task"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="overline text-[10px]">{label}</Label>
      {children}
    </div>
  );
}

function TaskDetailSheet({ task, users, isAdmin, onClose, onUpdate, onDelete, currentUser }) {
  const [comment, setComment] = useState("");
  const [newSub, setNewSub] = useState("");
  const open = !!task;
  if (!task) return <Sheet open={false} onOpenChange={onClose}><SheetContent className="p-0" /></Sheet>;

  const canEdit = isAdmin || task.assignee_id === currentUser?.user_id || task.creator_id === currentUser?.user_id;

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tasks/${task.task_id}/comments`, { text: comment });
      const r = await api.get(`/tasks/${task.task_id}`);
      onUpdate(task.task_id, {});
      // refresh via parent? we use onUpdate with task patch path - just set comments directly:
      task.comments = r.data.comments;
      setComment("");
    } catch (e) { toast.error("Cannot comment"); }
  };

  const toggleSub = (idx) => {
    const subs = (task.subtasks || []).map((s, i) => i === idx ? { ...s, done: !s.done } : s);
    onUpdate(task.task_id, { subtasks: subs });
  };
  const addSubtask = () => {
    if (!newSub.trim()) return;
    const subs = [...(task.subtasks || []), { id: Math.random().toString(36).slice(2, 10), title: newSub, done: false }];
    onUpdate(task.task_id, { subtasks: subs });
    setNewSub("");
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-[640px] p-0 overflow-y-auto" data-testid="task-detail-sheet">
        <div className="border-b border-neutral-200 p-6">
          <div className="overline mb-2">{task.task_id}</div>
          <SheetTitle asChild>
            <input
              className="font-display text-2xl font-semibold tracking-tight w-full bg-transparent outline-none disabled:opacity-100"
              defaultValue={task.title}
              disabled={!canEdit}
              onBlur={(e) => e.target.value !== task.title && onUpdate(task.task_id, { title: e.target.value })}
              data-testid="task-title-input"
            />
          </SheetTitle>
          <div className="flex flex-wrap gap-2 items-center mt-3">
            <Select value={task.status} onValueChange={(v) => onUpdate(task.task_id, { status: v })} disabled={!canEdit}>
              <SelectTrigger className="h-8 w-[140px]" data-testid="task-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={task.priority} onValueChange={(v) => onUpdate(task.task_id, { priority: v })} disabled={!canEdit}>
              <SelectTrigger className="h-8 w-[120px]" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4 text-neutral-400" />
              <Input
                type="date"
                className="h-8 w-[150px]"
                defaultValue={task.due_date || ""}
                onChange={(e) => onUpdate(task.task_id, { due_date: e.target.value || null })}
                disabled={!canEdit}
                data-testid="task-due-input"
              />
            </div>
            {isAdmin && (
              <Select value={task.assignee_id || ""} onValueChange={(v) => onUpdate(task.task_id, { assignee_id: v })}>
                <SelectTrigger className="h-8 w-[170px]" data-testid="task-assignee-select"><SelectValue placeholder="Assignee" /></SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.is_active).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {isAdmin && (
              <Button variant="ghost" size="sm" className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => onDelete(task.task_id)} data-testid="task-delete-button">
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete
              </Button>
            )}
          </div>
        </div>

        <div className="p-6 space-y-8">
          <section>
            <div className="overline mb-2">Description</div>
            <Textarea
              rows={4}
              defaultValue={task.description}
              disabled={!canEdit}
              onBlur={(e) => e.target.value !== task.description && onUpdate(task.task_id, { description: e.target.value })}
              data-testid="task-description-textarea"
              placeholder="Add context, links, acceptance criteria…"
            />
          </section>

          <section>
            <div className="overline mb-2">Subtasks ({(task.subtasks || []).length})</div>
            <div className="space-y-2">
              {(task.subtasks || []).map((s, i) => (
                <label key={s.id} className="flex items-center gap-3 py-1.5 group cursor-pointer">
                  <Checkbox checked={s.done} onCheckedChange={() => canEdit && toggleSub(i)} disabled={!canEdit} data-testid={`subtask-${i}-checkbox`} />
                  <span className={`text-sm ${s.done ? "line-through text-neutral-400" : ""}`}>{s.title}</span>
                </label>
              ))}
              {canEdit && (
                <div className="flex gap-2 pt-2">
                  <Input placeholder="Add subtask…" value={newSub} onChange={e => setNewSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSubtask()} data-testid="subtask-input" />
                  <Button variant="outline" onClick={addSubtask} data-testid="subtask-add-button">Add</Button>
                </div>
              )}
            </div>
          </section>

          <section>
            <div className="overline mb-2">Comments ({(task.comments || []).length})</div>
            <div className="space-y-3">
              {(task.comments || []).map((c) => (
                <div key={c.id} className="border border-neutral-200 rounded-md p-3">
                  <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1">
                    <span className="font-medium text-neutral-900">{c.user_name}</span>
                    · {new Date(c.created_at).toLocaleString()}
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{c.text}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Input placeholder="Write a comment…" value={comment} onChange={e => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} data-testid="comment-input" />
              <Button onClick={addComment} className="bg-neutral-950 hover:bg-neutral-800" data-testid="comment-submit">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
